import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, FileText, Loader2, ShieldCheck, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

export default function StudentDocUploadPage() {
  const { token } = useParams();
  const fileRef = useRef(null);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [category, setCategory] = useState("other");
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/public/lead-docs/${token}`);
      setInfo(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onPick = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File must be under 15MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      await api.post(`/public/lead-docs/${token}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded ✓");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F6F8FB] text-slate-400">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
      </div>
    );
  }
  if (error || !info) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F6F8FB] px-6 text-center text-sm text-slate-500">
        This upload link is invalid or has expired. Please ask your counsellor for a new link.
      </div>
    );
  }

  const categories = info.categories || {};

  return (
    <div className="min-h-screen bg-[#F6F8FB] py-10" data-testid="student-doc-upload-page">
      <div className="mx-auto max-w-md px-5">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-1 flex items-center gap-2 text-[#0A2540]">
            <ShieldCheck className="h-5 w-5 text-emerald-500" />
            <span className="text-xs uppercase tracking-[0.18em] text-slate-400">Secure upload</span>
          </div>
          <h1 className="font-display text-xl font-semibold text-[#0A2540]">
            Hi {info.lead_name}, upload your documents
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Pick a document type and upload a clear photo or PDF. JPEG, PNG, WEBP or PDF · up to 15MB.
          </p>

          <div className="mt-5 space-y-3">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-11" data-testid="student-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(categories).map(([k, label]) => (
                  <SelectItem key={k} value={k} data-testid={`student-cat-${k}`}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              className="hidden"
              onChange={(e) => onPick(e.target.files?.[0])}
              data-testid="student-file-input"
            />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="h-12 w-full bg-[#0A2540] text-base hover:bg-[#1E3A5F]"
              data-testid="student-upload-btn"
            >
              {uploading ? (
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              ) : (
                <Upload className="mr-2 h-5 w-5" />
              )}
              Upload document
            </Button>
          </div>

          {(info.uploaded || []).length > 0 && (
            <div className="mt-6">
              <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                Received ({info.uploaded.length})
              </div>
              <div className="space-y-1.5">
                {info.uploaded.map((d, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2" data-testid={`student-uploaded-${i}`}>
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                    <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-medium text-[#0A2540]">{d.filename}</div>
                      <div className="text-[10px] text-slate-400">{d.category_label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <p className="mt-4 text-center text-[11px] text-slate-400">
          Your documents are shared only with your consultancy.
        </p>
      </div>
    </div>
  );
}
